import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ResourceLibrary.css'; // Import component styles

const ResourceLibrary = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filteredResources, setFilteredResources] = useState([]);
  const [visibleCount, setVisibleCount] = useState(9);
  const [playingResource, setPlayingResource] = useState(null);
  const [downloadingResources, setDownloadingResources] = useState([]);
  const [viewModalResource, setViewModalResource] = useState(null);

  // Resource categories
  const categories = [
    { id: 'all', name: 'All Resources', icon: 'fa-th', count: 48 },
    { id: 'articles', name: 'Articles', icon: 'fa-newspaper', count: 15 },
    { id: 'videos', name: 'Videos', icon: 'fa-video', count: 12 },
    { id: 'worksheets', name: 'Worksheets', icon: 'fa-file-alt', count: 8 },
    { id: 'podcasts', name: 'Podcasts', icon: 'fa-podcast', count: 6 },
    { id: 'infographics', name: 'Infographics', icon: 'fa-chart-pie', count: 4 },
    { id: 'books', name: 'E-Books', icon: 'fa-book', count: 3 }
  ];

  // Sample resources data
  const resources = [
    // Articles
    {
      id: 1,
      title: 'Understanding Depression: A Comprehensive Guide',
      description: 'Learn about the causes, symptoms, and treatment options for depression.',
      category: 'articles',
      type: 'Article',
      readTime: '10 min read',
      author: 'Dr. Sarah Johnson',
      date: '2025-01-15',
      featured: true,
      tags: ['depression', 'mental-health', 'treatment'],
      icon: 'fa-newspaper',
      color: 'bg-blue-500',
      downloadable: true,
      format: 'PDF'
    },
    {
      id: 2,
      title: 'Anxiety Management Techniques',
      description: 'Practical strategies to manage and reduce anxiety in daily life.',
      category: 'articles',
      type: 'Article',
      readTime: '8 min read',
      author: 'Dr. Michael Chen',
      date: '2025-01-12',
      tags: ['anxiety', 'coping-strategies', 'self-help'],
      icon: 'fa-newspaper',
      color: 'bg-purple-500',
      downloadable: true,
      format: 'PDF'
    },
    {
      id: 3,
      title: 'The Science of Sleep and Mental Health',
      description: 'Explore the crucial connection between quality sleep and mental wellbeing.',
      category: 'articles',
      type: 'Article',
      readTime: '12 min read',
      author: 'Dr. Emily Roberts',
      date: '2025-01-10',
      tags: ['sleep', 'mental-health', 'wellness'],
      icon: 'fa-newspaper',
      color: 'bg-indigo-500',
      downloadable: true,
      format: 'PDF'
    },
    
    // Videos
    {
      id: 4,
      title: 'Mindfulness Meditation for Beginners',
      description: 'A guided introduction to mindfulness meditation practices.',
      category: 'videos',
      type: 'Video',
      duration: '15 minutes',
      author: 'Mindfulness Institute',
      date: '2025-01-14',
      featured: true,
      tags: ['mindfulness', 'meditation', 'stress-relief'],
      icon: 'fa-video',
      color: 'bg-teal-500',
      thumbnail: true
    },
    {
      id: 5,
      title: 'Understanding Panic Attacks',
      description: 'Educational video explaining panic attacks and coping mechanisms.',
      category: 'videos',
      type: 'Video',
      duration: '20 minutes',
      author: 'Mental Health Foundation',
      date: '2025-01-11',
      tags: ['panic-attacks', 'anxiety', 'education'],
      icon: 'fa-video',
      color: 'bg-red-500',
      thumbnail: true
    },
    {
      id: 6,
      title: 'Progressive Muscle Relaxation Tutorial',
      description: 'Step-by-step guide to progressive muscle relaxation technique.',
      category: 'videos',
      type: 'Video',
      duration: '25 minutes',
      author: 'Wellness Center',
      date: '2025-01-08',
      tags: ['relaxation', 'stress-management', 'technique'],
      icon: 'fa-video',
      color: 'bg-green-500',
      thumbnail: true
    },
    
    // Worksheets
    {
      id: 7,
      title: 'CBT Thought Record Worksheet',
      description: 'Track and challenge negative thought patterns with this CBT tool.',
      category: 'worksheets',
      type: 'Worksheet',
      pages: '4 pages',
      author: 'Therapy Resources',
      date: '2025-01-13',
      featured: true,
      tags: ['CBT', 'worksheet', 'therapy-tool'],
      icon: 'fa-file-alt',
      color: 'bg-yellow-500',
      downloadable: true,
      format: 'PDF'
    },
    {
      id: 8,
      title: 'Daily Mood Tracker',
      description: 'Monitor your daily moods and identify patterns over time.',
      category: 'worksheets',
      type: 'Worksheet',
      pages: '2 pages',
      author: 'Mental Health Team',
      date: '2025-01-09',
      tags: ['mood-tracking', 'self-monitoring', 'worksheet'],
      icon: 'fa-file-alt',
      color: 'bg-orange-500',
      downloadable: true,
      format: 'PDF'
    },
    {
      id: 9,
      title: 'Anxiety Coping Skills Worksheet',
      description: 'Develop and practice anxiety management strategies.',
      category: 'worksheets',
      type: 'Worksheet',
      pages: '3 pages',
      author: 'Anxiety Support Group',
      date: '2025-01-07',
      tags: ['anxiety', 'coping-skills', 'worksheet'],
      icon: 'fa-file-alt',
      color: 'bg-pink-500',
      downloadable: true,
      format: 'PDF'
    },
    
    // Podcasts
    {
      id: 10,
      title: 'Mental Health Matters: Episode 45',
      description: 'Discussion on building resilience and emotional strength.',
      category: 'podcasts',
      type: 'Podcast',
      duration: '45 minutes',
      author: 'Dr. James Wilson',
      date: '2025-01-15',
      tags: ['podcast', 'resilience', 'mental-health'],
      icon: 'fa-podcast',
      color: 'bg-purple-600',
      playable: true
    },
    {
      id: 11,
      title: 'The Anxiety Hour: Managing Social Anxiety',
      description: 'Expert insights on overcoming social anxiety challenges.',
      category: 'podcasts',
      type: 'Podcast',
      duration: '38 minutes',
      author: 'Anxiety Podcast Network',
      date: '2025-01-12',
      tags: ['podcast', 'social-anxiety', 'expert-advice'],
      icon: 'fa-podcast',
      color: 'bg-indigo-600',
      playable: true
    },
    
    // Infographics
    {
      id: 12,
      title: 'Signs of Depression Infographic',
      description: 'Visual guide to recognizing depression symptoms.',
      category: 'infographics',
      type: 'Infographic',
      format: 'PNG',
      author: 'Design Team',
      date: '2025-01-14',
      tags: ['infographic', 'depression', 'symptoms'],
      icon: 'fa-chart-pie',
      color: 'bg-blue-600',
      downloadable: true
    },
    {
      id: 13,
      title: 'Stress Management Techniques',
      description: 'Visual overview of effective stress management strategies.',
      category: 'infographics',
      type: 'Infographic',
      format: 'PNG',
      author: 'Wellness Design',
      date: '2025-01-10',
      tags: ['infographic', 'stress', 'management'],
      icon: 'fa-chart-pie',
      color: 'bg-green-600',
      downloadable: true
    },
    
    // E-Books
    {
      id: 14,
      title: 'The Complete Guide to Mindfulness',
      description: 'Comprehensive e-book on mindfulness practices and benefits.',
      category: 'books',
      type: 'E-Book',
      pages: '150 pages',
      author: 'Mindfulness Institute',
      date: '2025-01-05',
      featured: true,
      tags: ['ebook', 'mindfulness', 'comprehensive-guide'],
      icon: 'fa-book',
      color: 'bg-teal-600',
      downloadable: true,
      format: 'PDF'
    },
    {
      id: 15,
      title: 'Overcoming Anxiety: A Self-Help Guide',
      description: 'Step-by-step guide to understanding and managing anxiety.',
      category: 'books',
      type: 'E-Book',
      pages: '120 pages',
      author: 'Dr. Lisa Anderson',
      date: '2025-01-03',
      tags: ['ebook', 'anxiety', 'self-help'],
      icon: 'fa-book',
      color: 'bg-purple-700',
      downloadable: true,
      format: 'PDF'
    }
  ];

  // Filter resources based on category and search
  useEffect(() => {
    let filtered = resources;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(resource => resource.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(resource =>
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    setFilteredResources(filtered);
  }, [selectedCategory, searchQuery]);

  // Featured resources
  const featuredResources = resources.filter(r => r.featured);

  // Action Handlers
  const handleDownload = (resource) => {
    // Add to downloading state
    setDownloadingResources(prev => [...prev, resource.id]);
    
    // Simulate download
    setTimeout(() => {
      // Create a blob and download
      const content = `${resource.title}\n\n${resource.description}\n\nAuthor: ${resource.author}\nDate: ${resource.date}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resource.title.replace(/\s+/g, '_')}.${resource.format || 'txt'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Remove from downloading state
      setDownloadingResources(prev => prev.filter(id => id !== resource.id));
      
      // Show success message
      showNotification('success', `Downloaded: ${resource.title}`);
    }, 1500);
  };

  const handlePlay = (resource) => {
    setPlayingResource(resource);
    showNotification('info', `Now playing: ${resource.title}`);
    
    // Simulate play completion after duration
    setTimeout(() => {
      setPlayingResource(null);
      showNotification('info', 'Playback completed');
    }, 10000); // Stop after 10 seconds for demo
  };

  const handleView = (resource) => {
    setViewModalResource(resource);
  };

  const handleAccess = (resource) => {
    if (resource.downloadable) {
      handleDownload(resource);
    } else if (resource.playable) {
      handlePlay(resource);
    } else {
      handleView(resource);
    }
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 9);
  };

  // Notification system
  const [notifications, setNotifications] = useState([]);

  const showNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  // Get visible resources
  const visibleResources = filteredResources.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Resource Library
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Access our comprehensive collection of mental health resources, including articles, videos, 
            worksheets, and tools to support your wellness journey.
          </p>
        </div>

        {/* Featured Section */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fas fa-star text-yellow-500 mr-2"></i>
            Featured Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredResources.map(resource => (
              <div key={resource.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-5 border-l-4 border-yellow-500">
                <div className="flex items-start justify-between mb-3">
                  <div className={`${resource.color} text-white rounded-lg p-3`}>
                    <i className={`fas ${resource.icon} text-xl`}></i>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded">
                    Featured
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {resource.readTime || resource.duration || resource.pages}
                  </span>
                  <button 
                    onClick={() => handleAccess(resource)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition-colors"
                  >
                    {playingResource?.id === resource.id ? (
                      <>Playing <i className="fas fa-spinner fa-spin"></i></>
                    ) : downloadingResources.includes(resource.id) ? (
                      <>Downloading <i className="fas fa-spinner fa-spin"></i></>
                    ) : (
                      <>Access <i className="fas fa-arrow-right"></i></>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <i className="fas fa-search absolute left-3 top-3 text-gray-400"></i>
              </div>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Categories - Tablet Friendly */}
          <div className="lg:w-64">
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
              <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <i className={`fas ${category.icon} mr-3 ${
                        selectedCategory === category.id ? 'text-white' : 'text-gray-500'
                      }`}></i>
                      <span className="font-medium">{category.name}</span>
                    </div>
                    <span className={`text-xs ${
                      selectedCategory === category.id ? 'bg-blue-600' : 'bg-gray-200'
                    } px-2 py-1 rounded-full`}>
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md p-4 mt-4">
              <h3 className="font-semibold mb-3">Quick Stats</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Resources</span>
                  <span className="font-bold">48</span>
                </div>
                <div className="flex justify-between">
                  <span>New This Week</span>
                  <span className="font-bold">12</span>
                </div>
                <div className="flex justify-between">
                  <span>Downloads</span>
                  <span className="font-bold">1,234</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resources Grid/List - Tablet Optimized */}
          <div className="flex-1">
            {filteredResources.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <i className="fas fa-search text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-600">No resources found matching your criteria.</p>
                <button 
                  onClick={() => {setSelectedCategory('all'); setSearchQuery('');}}
                  className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' 
                : 'space-y-4'
              }>
                {visibleResources.map(resource => (
                  <div
                    key={resource.id}
                    className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-all ${
                      viewMode === 'list' ? 'p-4 flex items-center gap-4' : 'p-5'
                    }`}
                  >
                    {viewMode === 'grid' ? (
                      // Grid View
                      <>
                        <div className="flex items-start justify-between mb-3">
                          <div className={`${resource.color} text-white rounded-lg p-3`}>
                            <i className={`fas ${resource.icon} text-xl`}></i>
                          </div>
                          {resource.downloadable && (
                            <button 
                              onClick={() => handleDownload(resource)}
                              disabled={downloadingResources.includes(resource.id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                            >
                              {downloadingResources.includes(resource.id) ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-download"></i>
                              )}
                            </button>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                          {resource.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {resource.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <span>{resource.type}</span>
                          <span>{resource.date}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {resource.tags.slice(0, 2).map((tag, index) => (
                            <span
                              key={index}
                              className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded cursor-pointer hover:bg-gray-200"
                              onClick={() => setSearchQuery(tag)}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <button 
                          onClick={() => handleAccess(resource)}
                          disabled={playingResource?.id === resource.id || downloadingResources.includes(resource.id)}
                          className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {playingResource?.id === resource.id ? (
                            <>
                              <i className="fas fa-pause"></i> Playing
                            </>
                          ) : downloadingResources.includes(resource.id) ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Downloading
                            </>
                          ) : (
                            <>
                              <i className={`fas ${resource.playable ? 'fa-play' : resource.downloadable ? 'fa-download' : 'fa-eye'}`}></i>
                              {resource.playable ? 'Play' : resource.downloadable ? 'Download' : 'View'}
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      // List View
                      <>
                        <div className={`${resource.color} text-white rounded-lg p-3`}>
                          <i className={`fas ${resource.icon} text-xl`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {resource.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            {resource.description}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{resource.type}</span>
                            <span>{resource.author}</span>
                            <span>{resource.date}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {resource.downloadable && (
                            <button 
                              onClick={() => handleDownload(resource)}
                              disabled={downloadingResources.includes(resource.id)}
                              className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                            >
                              {downloadingResources.includes(resource.id) ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-download"></i>
                              )}
                            </button>
                          )}
                          <button 
                            onClick={() => handleAccess(resource)}
                            disabled={playingResource?.id === resource.id || downloadingResources.includes(resource.id)}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {playingResource?.id === resource.id ? (
                              <>
                                <i className="fas fa-pause"></i> Playing
                              </>
                            ) : (
                              <>
                                <i className={`fas ${resource.playable ? 'fa-play' : 'fa-eye'}`}></i>
                                {resource.playable ? 'Play' : 'View'}
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Load More */}
            {filteredResources.length > visibleCount && (
              <div className="text-center mt-8">
                <button 
                  onClick={handleLoadMore}
                  className="bg-white border-2 border-blue-500 text-blue-500 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 mx-auto"
                >
                  <i className="fas fa-plus-circle"></i>
                  Load More Resources ({filteredResources.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-6 md:p-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Need Help Finding Resources?
            </h2>
            <p className="text-gray-700 mb-6">
              Our team is here to help you find the right resources for your mental health journey.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center justify-center"
              >
                <i className="fas fa-envelope mr-2"></i>
                Contact Support
              </Link>
              <Link
                to="/self-help"
                className="bg-white text-blue-500 border-2 border-blue-500 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center justify-center"
              >
                <i className="fas fa-compass mr-2"></i>
                Explore Self-Help Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewModalResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">{viewModalResource.title}</h2>
              <button
                onClick={() => setViewModalResource(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                <span><i className="fas fa-user mr-1"></i> {viewModalResource.author}</span>
                <span><i className="fas fa-calendar mr-1"></i> {viewModalResource.date}</span>
                <span><i className="fas fa-clock mr-1"></i> {viewModalResource.readTime || viewModalResource.duration || viewModalResource.pages}</span>
              </div>
              <p className="text-gray-700 mb-6">{viewModalResource.description}</p>
              
              {/* Resource Content Placeholder */}
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className={`${viewModalResource.color} text-white rounded-full p-6 w-24 h-24 mx-auto mb-4 flex items-center justify-center`}>
                  <i className={`fas ${viewModalResource.icon} text-3xl`}></i>
                </div>
                <p className="text-gray-600 mb-4">
                  {viewModalResource.type === 'Video' && 'Video player would be embedded here'}
                  {viewModalResource.type === 'Article' && 'Full article content would be displayed here'}
                  {viewModalResource.type === 'Podcast' && 'Podcast player would be embedded here'}
                  {viewModalResource.type === 'Worksheet' && 'Worksheet preview would be shown here'}
                  {viewModalResource.type === 'Infographic' && 'Infographic image would be displayed here'}
                  {viewModalResource.type === 'E-Book' && 'E-book reader would be embedded here'}
                </p>
                {viewModalResource.downloadable && (
                  <button
                    onClick={() => {
                      handleDownload(viewModalResource);
                      setViewModalResource(null);
                    }}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <i className="fas fa-download mr-2"></i>
                    Download {viewModalResource.format}
                  </button>
                )}
              </div>
              
              {/* Tags */}
              <div className="mt-6 flex flex-wrap gap-2">
                {viewModalResource.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-gray-200"
                    onClick={() => {
                      setSearchQuery(tag);
                      setViewModalResource(null);
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`p-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] animate-slide-in ${
              notification.type === 'success' ? 'bg-green-500 text-white' :
              notification.type === 'error' ? 'bg-red-500 text-white' :
              'bg-blue-500 text-white'
            }`}
          >
            <i className={`fas ${
              notification.type === 'success' ? 'fa-check-circle' :
              notification.type === 'error' ? 'fa-exclamation-circle' :
              'fa-info-circle'
            }`}></i>
            <span>{notification.message}</span>
          </div>
        ))}
      </div>

      {/* Playing Audio Indicator */}
      {playingResource && (
        <div className="fixed bottom-20 left-4 bg-white rounded-lg shadow-lg p-4 flex items-center gap-4 z-40">
          <div className={`${playingResource.color} text-white rounded-lg p-3`}>
            <i className={`fas ${playingResource.icon} text-xl`}></i>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{playingResource.title}</p>
            <p className="text-sm text-gray-600">Now playing...</p>
          </div>
          <button
            onClick={() => {
              setPlayingResource(null);
              showNotification('info', 'Playback stopped');
            }}
            className="text-red-500 hover:text-red-600"
          >
            <i className="fas fa-stop-circle text-2xl"></i>
          </button>
        </div>
      )}
      
      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      `}</style>
    </div>
  );
};

export default ResourceLibrary;